/**
 * Проведение сессии ведущим (после старта через POST /api/sessions).
 *
 * Подключается к /ws?pin=<pin> с role=host (авто-join), показывает:
 * - lobby: PIN + QR + счётчик участников, кнопка «Начать» → host-команда start;
 * - question_active: вопрос (spectate) + живая статистика + ЖИВОЙ рейтинг, «Закрыть» → close;
 * - question_closed: reveal (верный вариант) + статистика + рейтинг, «Следующий» → next;
 * - finished: эффектный финал (FinishScreen, подиум топ-3) → по кнопке «Полная таблица»
 *   ResultsBoard (GET /api/sessions/{id}/results).
 *
 * И7-полировка: живой лидерборд (LiveLeaderboard), звук (reveal/finish), точное
 * «ответили N из M» для всех типов (stats.answered). Тайминги — серверные; хост лишь
 * инициирует шаги (realtime-protocol.md).
 */
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { loadStartOptions } from '../activeSession';
import { PinLobby } from '../components/PinLobby';
import { LobbyRoster } from '../components/LobbyRoster';
import { QuestionView } from '../components/QuestionView';
import { ResultsBoard } from '../components/ResultsBoard';
import { LiveLeaderboard } from '../components/LiveLeaderboard';
import { FinishScreen } from '../components/FinishScreen';
import { SoundToggle } from '../components/SoundToggle';
import { StatusPill } from '../components/ui';
import { useQuizSocket } from '../hooks';
import { getSessionResults, type GameMeta, type SessionResults } from '../api';
import {
  type Phase,
  type StartPayload,
  type PlayerPos,
  type QuestionPayload,
  type RevealPayload,
  type RosterEntry,
  type ScoreboardEntry,
  type ServerMessage,
  type WordCountEntry,
} from '../protocol';
import { errorMessage, wsErrorKey } from '../util';
import { ensureLanguages } from '../languages';
import { playFinish, playReveal } from '../sound';

export interface HostSessionProps {
  pin: string;
  sessionId: number;
  /** Вернуться к выбору/созданию игры. */
  onExit: () => void;
  /** Метаданные игры (для заголовка финала, если переданы). */
  game?: GameMeta;
}

export function HostSession({ pin, sessionId, onExit, game }: HostSessionProps): JSX.Element {
  const { t } = useTranslation();
  const [phase, setPhase] = useState<Phase>('lobby');
  const [participants, setParticipants] = useState(0);
  // И4 (Фаза 10): опция «счёт после каждого вопроса». До старта — значение
  // чекбокса лобби (шлётся в start-payload); после старта — синхронизируется
  // из state (reconnect-хост видит применённый режим). Фаза 13 (C2): лобби
  // предзаполняется выбором модала «Провести» (кабинет /me, startOptions) —
  // хост может изменить значения до «Начать игру». Решение 2026-11-02:
  // scorePause по умолчанию ВКЛЮЧЁН (null-запись/её отсутствие → true);
  // UI-галка «Скрывать счёт» инвертирована (см. PinLobby).
  const [scorePause, setScorePause] = useState(() => loadStartOptions()?.scorePause ?? true);
  const [lockAfterStart, setLockAfterStart] = useState(
    () => loadStartOptions()?.lockAfterStart ?? false,
  );
  // Решение 2026-11-02: строгие ники по умолчанию ВКЛЮЧЕНЫ (null → true).
  const [strictNick, setStrictNick] = useState(() => loadStartOptions()?.strictNick ?? true);
  // Фаза 16 (A): авто-переход после reveal — фиксируется при start.
  const [autoNext, setAutoNext] = useState(() => loadStartOptions()?.autoNext ?? false);
  const [roster, setRoster] = useState<RosterEntry[]>([]);
  const [question, setQuestion] = useState<QuestionPayload | null>(null);
  const [counts, setCounts] = useState<number[] | null>(null);
  // И8 (Фаза 10): живое облако word-вопроса из stats.words — для
  // spectate-предпросмотра ведущего (WordView); сбрасывается на каждом question.
  const [liveWords, setLiveWords] = useState<WordCountEntry[] | null>(null);
  // Фаза 14 (эпик B3): последний пакет позиций игроков run-вопроса (событие
  // positions ~5 Гц, только host-сокету); сбрасывается на каждом question.
  const [positions, setPositions] = useState<PlayerPos[] | null>(null);
  // 0.14.3 (S1/R2): серверное Unix-мс последнего пакета positions (payload.t) —
  // база лерп-интерполяции дотов (playback delay) и EMA-offset у RunHostView.
  const [positionsT, setPositionsT] = useState<number | null>(null);
  // Фильтр позиций по активному вопросу (handleMessage замыкает state — берём
  // свежий вопрос через ref, чтобы опоздавшие пакеты не попали в новый вопрос).
  const questionRef = useRef<QuestionPayload | null>(null);
  questionRef.current = question;
  const [answered, setAnswered] = useState<number | null>(null);
  const [reveal, setReveal] = useState<RevealPayload | null>(null);
  const [scoreboard, setScoreboard] = useState<ScoreboardEntry[]>([]);
  // Фаза 11 (И6прот): total топ-K-рейтинга — для подписи «Показан топ-25 из N».
  const [boardTotal, setBoardTotal] = useState(0);
  // Фаза 15 (C2): per-room счётчики входов из state.payload (additive, omitempty)
  // — «Игроков: N · Переподключений: M» live-панели; null = сервер полей не шлёт
  // (старый бэк) — счётчик скрыт.
  const [playersUnique, setPlayersUnique] = useState<number | null>(null);
  const [reconnects, setReconnects] = useState(0);
  const [results, setResults] = useState<SessionResults | null>(null);
  const [showFullResults, setShowFullResults] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingResults, setLoadingResults] = useState(false);
  const fetchedRef = useRef(false);

  function handleMessage(message: ServerMessage): void {
    switch (message.type) {
      case 'state':
        setPhase(message.payload.phase);
        setParticipants(message.payload.participants);
        // Фаза 16 (B2): предзагрузка грамматик подсветки по langs квиза
        // (событийно, без блокировки рендера; идемпотентно; квиз без кода
        // чанки не грузит) — хост видит те же блоки кода, что и игроки.
        if (message.payload.langs?.length) {
          void ensureLanguages(message.payload.langs);
        }
        // Фаза 15 (C2): счётчики входов (omitempty — отсутствие = не трогаем,
        // reconnects 0 опускается сервером и рендерится как 0).
        setPlayersUnique(message.payload.playersUnique ?? null);
        setReconnects(message.payload.reconnects ?? 0);
        // scorePause из state — только ВНЕ lobby: до старта серверный флаг
        // всегда false, и каждый join игрока (broadcast state) сбрасывал бы
        // включённый хостом чекбокс. После старта чекбокса уже нет — флаг
        // информационный (reconnect-хост).
        if (message.payload.phase !== 'lobby') {
          setScorePause(message.payload.scorePause ?? false);
        }
        break;
      case 'question':
        setQuestion(message.payload);
        setCounts(null);
        setLiveWords(null);
        setPositions(null);
        setPositionsT(null);
        setAnswered(null);
        setReveal(null);
        setError(null);
        break;
      case 'positions':
        // Фаза 14 (эпик A2/B3): коалесированный пакет позиций run-вопроса —
        // RunHostView рисует доты всех игроков и счётчики углов. Финальный
        // пакет при close замораживает доты (reveal приходит следом).
        // 0.14.3 (S1/R2): t — серверное время сборки (интерполяция + EMA-offset).
        if (questionRef.current?.questionId === message.payload.questionId) {
          setPositions(message.payload.players);
          setPositionsT(message.payload.t);
        }
        break;
      case 'stats':
        // counts — только choice/poll; answered — все типы (И7, опц-улучшение);
        // words — живое облако word-вопроса (И8).
        setCounts(message.payload.counts ?? null);
        setLiveWords(message.payload.words ?? null);
        setAnswered(message.payload.answered ?? null);
        break;
      case 'reveal':
        setReveal(message.payload);
        playReveal();
        break;
      case 'scoreboard':
        // Фаза 11 (И6прот): при N>K entries — топ-25 (length != числу игроков);
        // участники — только из state.participants (case 'state').
        setScoreboard(message.payload.entries);
        setBoardTotal(message.payload.total ?? 0);
        break;
      case 'roster':
        setRoster(message.payload.entries);
        break;
      case 'roster_delta': {
        // Фаза 15 (A1/A3): дельты — upsert записи по playerId (join — новый
        // слот, rename — обновлённый ник/nickLocked, update — статус слота
        // active/dropped при отвале/возврате; рассылается в любой фазе).
        const d = message.payload.entry;
        setRoster((prev) => {
          const i = prev.findIndex((e) => e.playerId === d.playerId);
          if (i === -1) return [...prev, d];
          const next = [...prev];
          next[i] = d;
          return next;
        });
        break;
      }
      case 'finished':
        playFinish();
        void fetchResults();
        break;
      case 'error': {
        // И5: nick_locked/duplicate_nick локализуем; прочее — сырое сообщение.
        const key = wsErrorKey(message.payload.code);
        setError(key ? t(key) : message.payload.message);
        break;
      }
      default:
        break;
    }
  }

  const { status, sendHostCommand, sendRenamePlayer, sendKick } = useQuizSocket(pin, 'host', {
    onMessage: handleMessage,
  });

  /**
   * Фаза 15 (B2): локальное скрытие строки сразу после подтверждения кика.
   * Сервер НЕ рассылает roster_delta о кике (приватность факта для других), а
   * полный roster-снапшот без кикнутого придёт только при следующем join/rename/
   * reconnect — без этого строка висела бы «живой» до конца сессии.
   *
   * 15.1: аналогично прячем строку и из live-рейтинга (событие scoreboard при
   * кике сервер тоже не рассылает — кикнутый исключается только из БУДУЩИХ
   * рассылок close/next, engine.rankedLocked). Ник — из roster-записи.
   */
  function handleKick(playerId: number): void {
    sendKick(playerId);
    const nick = roster.find((e) => e.playerId === playerId)?.nick;
    setRoster((prev) => prev.filter((e) => e.playerId !== playerId));
    if (nick !== undefined) {
      setScoreboard((prev) => prev.filter((e) => e.nick !== nick));
    }
  }

  async function fetchResults(): Promise<void> {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    setLoadingResults(true);
    try {
      setResults(await getSessionResults(sessionId));
    } catch (e) {
      setError(errorMessage(e));
      fetchedRef.current = false;
    } finally {
      setLoadingResults(false);
    }
  }

  // «ответили N из M»: приоритет — точный answered (все типы); иначе сумма counts.
  const answeredCount =
    answered !== null ? answered : counts ? counts.reduce((a, b) => a + b, 0) : null;

  /**
   * Start-payload лобби (Фаза 13, C2): только включённые флаги (сервер читает
   * omitempty); все выключены → undefined (legacy-совместимость, прежний
   * тест «start с пустым payload»).
   */
  function startPayload(): StartPayload | undefined {
    if (!scorePause && !lockAfterStart && !strictNick && !autoNext) return undefined;
    return {
      ...(scorePause ? { scorePause: true } : {}),
      ...(lockAfterStart ? { lockAfterStart: true } : {}),
      ...(strictNick ? { strictNick: true } : {}),
      ...(autoNext ? { autoNext: true } : {}),
    };
  }

  // ─── Финал: эффектный экран (FinishScreen) с переходом к полной таблице ───────
  if (results) {
    // Источник данных подиума — итоговый scoreboard (событие scoreboard после finished);
    // при пустом (редкий случай) восстанавливаем из results.players.
    const finishEntries: ScoreboardEntry[] =
      scoreboard.length > 0
        ? scoreboard
        : results.players.map((p) => ({ nick: p.nickname, score: p.score }));

    if (!showFullResults) {
      return (
        <main className="card card--wide">
          <header className="screen-head">
            <h2 className="block-title">{game?.title ?? t('host.session.gameDefault')}</h2>
            <div className="screen-head-meta">
              <SoundToggle />
              <button type="button" className="btn btn--ghost" onClick={onExit}>
                {t('host.session.newGame')}
              </button>
            </div>
          </header>
          <FinishScreen
            entries={finishEntries}
            actions={
              <button
                type="button"
                className="btn"
                onClick={() => setShowFullResults(true)}
                data-testid="show-full-results"
              >
                {t('host.session.fullTable')}
              </button>
            }
          />
        </main>
      );
    }
    return (
      <main className="card card--wide">
        <header className="screen-head">
          <h2 className="block-title">{t('host.session.resultsTitle')}</h2>
          <div className="screen-head-meta">
            <SoundToggle />
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => setShowFullResults(false)}
            >
              {t('host.session.toPodium')}
            </button>
            <button type="button" className="btn btn--ghost" onClick={onExit}>
              {t('host.session.newGame')}
            </button>
          </div>
        </header>
        <ResultsBoard results={results} />
      </main>
    );
  }

  return (
    <main className="card card--wide">
      <header className="screen-head">
        <h2 className="block-title">{t('host.session.title')}</h2>
        <div className="screen-head-meta">
          <SoundToggle />
          <StatusPill status={status} />
          <button type="button" className="btn btn--ghost" onClick={onExit}>
            {t('common.exit')}
          </button>
        </div>
      </header>

      {phase === 'lobby' ? (
        <>
          <PinLobby
            pin={pin}
            participants={participants}
            error={error}
            scorePause={scorePause}
            onScorePauseChange={setScorePause}
            autoNext={autoNext}
            onAutoNextChange={setAutoNext}
            lockAfterStart={lockAfterStart}
            onLockAfterStartChange={setLockAfterStart}
            strictNick={strictNick}
            onStrictNickChange={setStrictNick}
            onStart={() => sendHostCommand('start', startPayload())}
          />
          {/* И5: доска участников лобби + inline-переименование любого игрока.
              Фаза 15 (A2/B2): статусы отвала + кнопка «удалить» (kick). */}
          <LobbyRoster
            entries={roster}
            onRenamePlayer={sendRenamePlayer}
            onKickPlayer={handleKick}
          />
        </>
      ) : null}

      {(phase === 'question_active' || phase === 'question_closed') && question ? (
        <>
          {/* Фаза 15 (C2): счётчик «Игроков / Переподключений» + пороговое
              предупреждение M > 3×N («много переподключений — сети рвутся»). */}
          {playersUnique !== null ? (
            <p className="host-live-counters" data-testid="host-live-counters">
              {t('host.live.reconnects', { players: playersUnique, reconnects })}
            </p>
          ) : null}
          {playersUnique !== null && reconnects > 3 * playersUnique ? (
            <p className="host-live-counters host-live-counters--warn" role="status">
              <span data-testid="host-live-reconnects-warning">
                ⚠ {t('host.live.reconnectsWarning')}
              </span>
            </p>
          ) : null}
          {phase === 'question_active' ? (
            <div className="btn-row">
              <button type="button" className="btn" onClick={() => sendHostCommand('close')}>
                {t('host.session.closeQuestion')}
              </button>
            </div>
          ) : null}

          {/* И4: при включённой лидерборд-паузе вопрос на экране закрытия
              полупрозрачен — акцент переносится на топ счёта ниже. */}
          <div
            className={phase === 'question_closed' && scorePause ? 'q-view-dim' : undefined}
            data-testid={phase === 'question_closed' && scorePause ? 'q-view-dim' : undefined}
          >
            <QuestionView
              question={question}
              variant="spectate"
              counts={counts}
              reveal={reveal}
              liveWords={liveWords}
              participants={participants}
              positions={positions}
              positionsT={positionsT}
              frozen={phase === 'question_closed' || reveal !== null}
            />
          </div>

          {/* «ответили N из M»: works for all types via stats.answered (И7). */}
          {answeredCount !== null ? (
            <p className="muted small q-answered" data-testid="answered-summary">
              {t('host.session.answered', { answered: answeredCount, total: participants })}
            </p>
          ) : null}

          {phase === 'question_closed' ? (
            <div className="btn-row">
              {/* Баг-К (Фаза 10, И1): на последнем вопросе кнопка означает финал. */}
              <button type="button" className="cta" onClick={() => sendHostCommand('next')}>
                {question.index + 1 >= question.total
                  ? t('host.session.finishQuiz')
                  : t('host.session.nextQuestion')}
              </button>
            </div>
          ) : null}

          {/* Фаза 15.1 (решение владельца): ростер в фазе вопроса НЕ рендерится
              над вопросом — статусы «отвалился» и кик врезаны в «Живой рейтинг»
              ниже (один список). Пока scoreboard пуст (первый вопрос ещё не
              закрыт), LiveLeaderboard сам показывает fallback-строки из roster. */}
          {scoreboard.length > 0 || roster.length > 0 ? (
            <>
              <LiveLeaderboard
                entries={scoreboard}
                title={t('host.session.liveLeaderboardTitle')}
                roster={roster}
                onKickPlayer={handleKick}
              />
              {boardTotal > scoreboard.length ? (
                <p className="muted small" data-testid="top-shown">
                  {t('player.topShown', { shown: scoreboard.length, total: boardTotal })}
                </p>
              ) : null}
            </>
          ) : null}
        </>
      ) : null}

      {phase === 'finished' && !results ? (
        <p className="muted">
          {loadingResults ? t('host.session.loadingResults') : t('host.session.finished')}
        </p>
      ) : null}

      {error ? (
        <p className="error" role="alert">
          {error}
        </p>
      ) : null}
    </main>
  );
}
